/**
 * Where Edit > Undo / Redo should apply.
 *
 * A writable text field keeps the browser's own history. That covers input
 * and textarea, and the contenteditable ZwInput / ZwTextarea fields. Everything
 * else, including the ProseMirror manuscript and notes editors, is the editor
 * command. Native menu ids
 * `edit-undo` and `edit-redo` match `linux_menu.rs`.
 */

import { isJust, type Maybe } from "./maybe";

/** Native menu ids. Keep in sync with `linux_menu.rs`. */
export const EDIT_UNDO_MENU_ID = "edit-undo";
export const EDIT_REDO_MENU_ID = "edit-redo";

export type EditHistoryKind = "undo" | "redo";

export type EditHistorySurface = "native-field" | "editor";

/** Root class prosemirror-view puts on every rich editor. */
export const RICH_EDITOR_SELECTOR = ".ProseMirror";

/** Snapshot of `document.activeElement`, taken at the DOM boundary. */
export type EditFieldSnapshot = {
  readonly tag: string;
  readonly disabled: boolean;
  readonly readOnly: boolean;
  /** `isContentEditable` on the focused element. */
  readonly contentEditable: boolean;
  /** True when the element sits inside a ProseMirror editor. */
  readonly insideRichEditor: boolean;
};

export function editHistorySurface(
  active: Maybe<EditFieldSnapshot>,
): EditHistorySurface {
  if (isJust(active) === false) return "editor";
  const field = active.value;
  if (field.insideRichEditor) return "editor";
  if (field.disabled || field.readOnly) return "editor";
  if (field.contentEditable) return "native-field";
  const tag = field.tag.toLowerCase();
  if (tag !== "input" && tag !== "textarea") return "editor";
  return "native-field";
}
